import React from 'react';
import { motion } from 'framer-motion';
import { Scale, Building2, Server, FileText, Shield, ExternalLink, Globe } from 'lucide-react';
import BackToHomeLink from '../components/BackToHomeLink';

const Section = ({ icon: Icon, title, children, delay = 0 }) => (
  <motion.section
    className="mb-10"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
  >
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-[#a5400e]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="text-[#a5400e]" size={20} />
      </div>
      <div className="flex-1">
        <h2 className="text-xl font-semibold text-[#5c2810] mb-3">{title}</h2>
        <div className="text-[#7a4a3a] leading-relaxed">{children}</div>
      </div>
    </div>
  </motion.section>
);

const MentionsLegales = () => {
  const currentDate = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F0E8] to-[#F8F4ED] pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a5400e]/10 mb-4">
            <Scale className="text-[#a5400e]" size={18} />
            <span className="text-sm text-[#a5400e] font-medium">Document juridique</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif text-[#5c2810] mb-4">
            Mentions Légales
          </h1>
          <p className="text-[#7a4a3a]">
            Dernière mise à jour : {currentDate}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8 md:p-12 border border-[#a5400e]/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Section icon={Globe} title="1. Présentation du site" delay={0.1}>
            <p className="mb-4">
              En vertu de l'article 6 de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique (LCEN),
              il est précisé aux utilisateurs du site <strong>s.belliard-reynes.fr</strong> l'identité des différents intervenants
              dans le cadre de sa réalisation et de son suivi.
            </p>
            <p>
              {"Ce site présente des prestations d’accompagnement de bien-être en neuro-thérapie et kinésiologie."}
            </p>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Building2} title="2. Éditeur du site" delay={0.2}>
            <div className="bg-[#F5F0E8] p-6 rounded-xl space-y-2">
              <p><strong>Propriétaire et Éditeur</strong> : Siriane Belliard-Reynes</p>
              <p><strong>Adresse professionnelle</strong> : 9 rue du Docteur Mourier, 77430 Champagne-sur-Seine</p>
              <p><strong>SIRET</strong> : 853 464 873 00035</p>
              <p><strong>Code APE</strong> : 8690F (Autres activités pour la santé humaine)</p>
              <p><strong>Email</strong> : neuro.endo.therapeute@gmail.com</p>
              <p><strong>Téléphone</strong> : 06 36 14 31 26</p>
            </div>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Server} title="3. Hébergement" delay={0.3}>
            <div className="bg-[#F5F0E8] p-6 rounded-xl space-y-2">
              <p><strong>Hébergeur</strong> : IONOS SE</p>
              <p><strong>Adresse</strong> : 7 Place de la Gare, 57200 Sarreguemines, France</p>
              <p><strong>Site web</strong> : <a href="https://www.ionos.fr" target="_blank" rel="noopener noreferrer" className="text-[#a5400e] hover:underline inline-flex items-center gap-1">www.ionos.fr <ExternalLink size={14} /></a></p>
              <p className="text-sm text-[#7a4a3a]/70 mt-2">
                IONOS est un hébergeur certifié ISO 27001 et conforme au RGPD pour l'hébergement de données personnelles.
              </p>
            </div>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={FileText} title="4. Conditions d'utilisation" delay={0.4}>
            <p className="mb-4">
              L'utilisation du site implique l'acceptation pleine et entière des conditions générales d'utilisation décrites ci-après.
              Ces conditions sont susceptibles d'être modifiées ou complétées à tout moment, les utilisateurs sont invités à les consulter régulièrement.
            </p>
            <p className="mb-4">
              Le site est normalement accessible à tout moment. Une interruption pour raison de maintenance technique peut être décidée
              sans préavis. Les horaires d'intervention seront communiqués si possible.
            </p>
            <div className="bg-[#a5400e]/5 p-4 rounded-lg border-l-4 border-[#a5400e]">
              <p className="text-sm">
                <strong>Important</strong> : {"Les informations fournies sur ce site sont à titre informatif et ne constituent pas un avis médical. Elles ne remplacent pas une consultation avec un professionnel de santé qualifié. Les accompagnements de bien-être présentés ne remplacent ni un diagnostic, ni un traitement, ni un suivi médical ou psychologique et ne doivent pas conduire à interrompre des soins prescrits."}
              </p>
            </div>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Shield} title="5. Propriété intellectuelle" delay={0.5}>
            <p className="mb-4">
              Siriane Belliard-Reynes est propriétaire des droits de propriété intellectuelle ou détient les droits d'usage sur tous
              les éléments accessibles sur le site : textes, images, graphismes, logo, icônes, sons, logiciels.
            </p>
            <p className="mb-4">
              Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site,
              quel que soit le moyen ou le procédé utilisé, est interdite sans l'autorisation écrite préalable.
            </p>
            <p>
              Toute exploitation non autorisée du site ou de l'un quelconque des éléments qu'il contient sera considérée comme constitutive
              d'une contrefaçon et poursuivie conformément aux dispositions des articles L.335-2 et suivants du Code de Propriété Intellectuelle.
            </p>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Shield} title="6. Responsabilité et limitations" delay={0.6}>
            <p className="mb-4">
              Les informations fournies sur le site le sont à titre indicatif. L'exactitude, la complétude et l'actualité
              des informations ne peuvent être garanties malgré le soin apporté à leur publication.
            </p>
            <p className="mb-4">
              Siriane Belliard-Reynes décline toute responsabilité pour tout dommage résultant notamment d'une imprécision
              ou inexactitude des informations disponibles sur le site.
            </p>
            <p>
              Le site peut contenir des liens vers d'autres sites internet. Siriane Belliard-Reynes ne dispose d'aucun moyen
              pour contrôler ces sites externes et ne répond pas de leur contenu ou disponibilité.
            </p>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Scale} title="7. Droit applicable et juridiction" delay={0.7}>
            <p>
              Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.
              Tout litige relatif à l'utilisation du site sera soumis à la compétence exclusive des juridictions de Paris,
              sauf disposition légale contraire.
            </p>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Shield} title="8. Traitement des données personnelles" delay={0.8}>
            <p className="mb-4">
              Pour plus d'informations sur la collecte et le traitement des données personnelles, veuillez consulter notre
              <a href="/politique-confidentialite" className="text-[#a5400e] hover:underline">Politique de Confidentialité</a>.
            </p>
            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez de droits sur vos données personnelles
              (accès, rectification, effacement, limitation, portabilité, opposition) que vous pouvez exercer à tout moment.
            </p>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={Shield} title="9. Accessibilité" delay={0.9}>
            <p className="mb-4">
              Le site s'efforce de respecter les critères d'accessibilité du RGAA (Référentiel Général d'Amélioration de l'Accessibilité)
              en vue d'une mise en conformité avec le niveau AA des WCAG 2.1.
            </p>
            <p>
              Si vous rencontrez des difficultés d'accessibilité, n'hésitez pas à nous contacter à
              neuro.endo.therapeute@gmail.com pour que nous puissions vous aider et améliorer le site.
            </p>
          </Section>

          <div className="h-px bg-gradient-to-r from-transparent via-[#a5400e]/20 to-transparent my-8" />

          <Section icon={FileText} title="10. Contact" delay={1.0}>
            <p className="mb-4">
              Pour toute question concernant ces mentions légales ou le fonctionnement du site, vous pouvez nous contacter :
            </p>
            <div className="bg-[#F5F0E8] p-6 rounded-xl space-y-2">
              <p><strong>Email</strong> : neuro.endo.therapeute@gmail.com</p>
              <p><strong>Téléphone</strong> : 06 36 14 31 26</p>
              <p><strong>Adresse</strong> : 9 rue du Docteur Mourier, 77430 Champagne-sur-Seine</p>
            </div>
          </Section>
        </motion.div>

        {/* Back to home */}
        <motion.div
          className="mt-12 flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <BackToHomeLink variant="solid" />
        </motion.div>
      </div>
    </div>
  );
};

export default MentionsLegales;
